/* global Word */
import {
  AnalyzeResult,
  AnalyzeResultsStore,
  CitationType,
  LawCase,
  CaseReference,
  ResolutionStatus,
} from "../models/benchly.models";

/**
 * Service for managing citation lookup results in Word XML Custom Parts
 */
export class CitationStorageService {
  private static readonly NAMESPACE = "http://schemas.beyondassured.com/citation-results";
  private static readonly ROOT_ELEMENT = "CitationResults";
  private static readonly VERSION = "1.0";

  /**
   * Stores citation lookup results from API batch response in the document's XML custom parts.
   * Replaces any existing results with the new batch from the API.
   * @param results Array of citation lookup results from API batch response
   * @returns Promise that resolves when all results are stored
   */
  public static async storeCitationResults(results: AnalyzeResult[]): Promise<string> {
    try {
      let result = "";
      await Word.run(async (context) => {
        const customXmlParts = context.document.customXmlParts;
        customXmlParts.load("items");
        await context.sync();

        console.log(`Found ${customXmlParts.items.length} existing XML parts`);

        // Find and delete existing custom XML part with our namespace
        let deletedExisting = false;
        for (let i = 0; i < customXmlParts.items.length; i++) {
          const part = customXmlParts.items[i];
          try {
            part.load("namespaceUri");
            await context.sync();

            if (part.namespaceUri === this.NAMESPACE) {
              console.log("Deleting existing citation XML part");
              part.delete();
              await context.sync();
              deletedExisting = true;
              break;
            }
          } catch (partError) {
            console.warn(`Error checking part ${i}:`, partError);
            continue;
          }
        }

        if (deletedExisting) {
          console.log("Existing citation data cleared");
        }

        // Create new store with the batch results
        const store: AnalyzeResultsStore = {
          version: this.VERSION,
          lastUpdated: new Date().toISOString(),
          results: results,
          fuzzySearchCompleted: false, // Reset flag when storing new results from exact search
        };

        // Create XML and add new custom part
        const xml = this.storeToXml(store);
        console.log("Generated XML length:", xml.length);

        customXmlParts.add(xml);
        await context.sync();

        console.log(`Successfully stored ${results.length} citation results from API batch`);
        result = `Stored ${results.length} citation results from API batch`;
      });
      return result;
    } catch (error) {
      console.error("Error in storeCitationResults:", error);
      throw error;
    }
  }

  /**
   * Retrieves a citation lookup result by document reference ID
   * @param documentReferenceId The ID of the document reference
   * @returns Promise that resolves to the citation result, or null if not found
   */
  public static async getCitationResult(
    documentReferenceId: string
  ): Promise<AnalyzeResult | null> {
    try {
      return await Word.run(async (context) => {
        const customXmlParts = context.document.customXmlParts;
        customXmlParts.load("items");
        await context.sync();

        console.log(`Looking for citation result with documentReferenceId: ${documentReferenceId}`);

        // Find our custom XML part
        for (let i = 0; i < customXmlParts.items.length; i++) {
          const part = customXmlParts.items[i];
          try {
            part.load("namespaceUri");
            await context.sync();

            if (part.namespaceUri === this.NAMESPACE) {
              console.log("Found citation XML part, loading XML content");

              // Load XML with proper error handling
              part.load("xml");
              await context.sync();

              if (part.xml && part.xml.trim() !== "") {
                const store = this.parseXmlToStore(part.xml);
                console.log(`Parsed store with ${store.results.length} results`);

                const result = store.results.find(
                  (r) => r.documentReferenceId === documentReferenceId
                );
                if (result) {
                  console.log("Found matching citation result");
                  return result;
                } else {
                  console.log("No citation result found for the given documentReferenceId");
                  return null;
                }
              } else {
                console.warn("Citation XML part exists but has no content");
                return null;
              }
            }
          } catch (partError) {
            console.error(`Error processing XML part ${i}:`, partError);
            continue;
          }
        }

        console.log("No citation XML part found in document");
        return null;
      });
    } catch (error) {
      console.error("Error in getCitationResult:", error);
      return null;
    }
  }

  /**
   * Checks if citation results have been stored in the document
   * @returns Promise that resolves to true if citation results exist, false otherwise
   */
  public static async citationResultsExist(): Promise<boolean> {
    try {
      return await Word.run(async (context) => {
        console.log("Checking if citation results exist...");

        const customXmlParts = context.document.customXmlParts;
        customXmlParts.load("items");
        await context.sync();

        console.log(`Found ${customXmlParts.items.length} custom XML parts`);

        // Check if any part has our namespace
        for (let i = 0; i < customXmlParts.items.length; i++) {
          const part = customXmlParts.items[i];

          try {
            part.load("namespaceUri");
            await context.sync();

            console.log(`Checking part ${i + 1} namespace: "${part.namespaceUri}"`);

            if (part.namespaceUri === this.NAMESPACE) {
              console.log("Citation results found in document");
              return true;
            }
          } catch (partError) {
            console.warn(`Error checking part ${i + 1}:`, partError);
            continue;
          }
        }

        console.log("No citation results found in document");
        return false;
      });
    } catch (error) {
      console.error("Error checking if citation results exist:", error);
      return false;
    }
  }

  /**
   * Retrieves all citation lookup results from the document
   * @returns Promise that resolves to array of all citation results
   */
  public static async getAllCitationResults(): Promise<AnalyzeResultsStore> {
    try {
      return await Word.run(async (context) => {
        console.log("Loading custom XML parts for citation results...");

        const customXmlParts = context.document.customXmlParts;
        customXmlParts.load("items");
        await context.sync();

        console.log(`Found ${customXmlParts.items.length} custom XML parts`);

        // Find our custom XML part
        for (let i = 0; i < customXmlParts.items.length; i++) {
          const part = customXmlParts.items[i];
          console.log(`Checking XML part ${i + 1}`);

          try {
            part.load("namespaceUri");
            await context.sync();

            console.log(`Part namespace: "${part.namespaceUri}"`);

            if (part.namespaceUri === this.NAMESPACE) {
              console.log("Found citation XML part, loading XML content...");

              // Load XML with proper error handling
              part.load("xml");
              await context.sync();

              console.log("XML loaded, checking if available...");

              // Check if XML property is properly loaded
              if (part.xml !== undefined && part.xml !== null) {
                console.log(`XML content loaded, length: ${part.xml.length}`);

                if (part.xml.trim().length > 0) {
                  try {
                    const store = this.parseXmlToStore(part.xml);
                    console.log(`Successfully parsed ${store.results.length} citation results`);
                    return store;
                  } catch (parseError) {
                    console.error("Error parsing XML content:", parseError);
                    return {
                      version: this.VERSION,
                      lastUpdated: new Date().toISOString(),
                      results: [],
                    };
                  }
                } else {
                  console.warn("XML content is empty");
                  return {
                    version: this.VERSION,
                    lastUpdated: new Date().toISOString(),
                    results: [],
                  };
                }
              } else {
                console.error("XML property is undefined/null after loading");
                return {
                  version: this.VERSION,
                  lastUpdated: new Date().toISOString(),
                  results: [],
                };
              }
            }
          } catch (partError) {
            console.error(`Error processing XML part ${i + 1}:`, partError);
            continue; // Try next part
          }
        }

        console.log("No citation XML part found with matching namespace");
        return { version: this.VERSION, lastUpdated: new Date().toISOString(), results: [] };
      });
    } catch (error) {
      console.error("Critical error in getAllCitationResults:", error);
      return { version: this.VERSION, lastUpdated: new Date().toISOString(), results: [] };
    }
  }

  /**
   * Deletes a citation result by document reference ID
   * @param documentReferenceId The ID of the document reference
   * @returns Promise that resolves when the result is deleted
   */
  public static async deleteCitationResult(documentReferenceId: string): Promise<void> {
    try {
      await Word.run(async (context) => {
        const customXmlParts = context.document.customXmlParts;
        customXmlParts.load("items");
        await context.sync();

        console.log(
          `Attempting to delete citation result for documentReferenceId: ${documentReferenceId}`
        );

        // Find our custom XML part
        for (let i = 0; i < customXmlParts.items.length; i++) {
          const part = customXmlParts.items[i];
          try {
            part.load("namespaceUri");
            await context.sync();

            if (part.namespaceUri === this.NAMESPACE) {
              console.log("Found citation XML part, loading content for deletion");

              part.load("xml");
              await context.sync();

              if (part.xml && part.xml.trim() !== "") {
                const store = this.parseXmlToStore(part.xml);
                const originalCount = store.results.length;
                store.results = store.results.filter(
                  (r) => r.documentReferenceId !== documentReferenceId
                );
                store.lastUpdated = new Date().toISOString();

                console.log(`Filtered results: ${originalCount} → ${store.results.length}`);

                // Delete old part
                part.delete();
                await context.sync();

                // Add updated part if there are still results
                if (store.results.length > 0) {
                  const xml = this.storeToXml(store);
                  customXmlParts.add(xml);
                  await context.sync();
                  console.log(
                    `Updated citation XML part with ${store.results.length} remaining results`
                  );
                } else {
                  console.log("No results remaining, citation XML part deleted completely");
                }

                console.log(
                  `Successfully deleted citation result for document reference: ${documentReferenceId}`
                );
                return;
              } else {
                console.warn("Citation XML part exists but has no content");
                return;
              }
            }
          } catch (partError) {
            console.error(`Error processing XML part ${i} during deletion:`, partError);
            continue;
          }
        }

        console.log("No citation XML part found to delete from");
      });
    } catch (error) {
      console.error("Error in deleteCitationResult:", error);
      throw error;
    }
  }

  /**
   * Updates a single citation result in the document's XML custom parts.
   * @param updatedResult The updated citation result
   * @returns Promise that resolves when the result is updated
   */
  public static async updateCitationResult(updatedResult: AnalyzeResult): Promise<void> {
    try {
      await Word.run(async (context) => {
        const customXmlParts = context.document.customXmlParts;
        customXmlParts.load("items");
        await context.sync();

        console.log(
          `Updating citation result for documentReferenceId: ${updatedResult.documentReferenceId}`
        );

        // Find our custom XML part
        for (let i = 0; i < customXmlParts.items.length; i++) {
          const part = customXmlParts.items[i];
          try {
            part.load("namespaceUri");
            await context.sync();

            if (part.namespaceUri === this.NAMESPACE) {
              console.log("Found citation XML part, loading content for update");

              part.load("xml");
              await context.sync();

              if (part.xml && part.xml.trim() !== "") {
                const store = this.parseXmlToStore(part.xml);

                // Find and update the specific citation
                const index = store.results.findIndex(
                  (r) => r.documentReferenceId === updatedResult.documentReferenceId
                );

                if (index !== -1) {
                  store.results[index] = updatedResult;
                  store.lastUpdated = new Date().toISOString();

                  console.log(`Updated citation at index ${index}`);

                  // Delete old part
                  part.delete();
                  await context.sync();

                  // Add updated part
                  const xml = this.storeToXml(store);
                  customXmlParts.add(xml);
                  await context.sync();

                  console.log(
                    `Successfully updated citation result for: ${updatedResult.documentReferenceId}`
                  );
                } else {
                  console.warn(
                    `Citation with documentReferenceId ${updatedResult.documentReferenceId} not found`
                  );
                }
                return;
              }
            }
          } catch (partError) {
            console.error(`Error processing XML part ${i} during update:`, partError);
            continue;
          }
        }

        console.log("No citation XML part found to update");
      });
    } catch (error) {
      console.error("Error in updateCitationResult:", error);
      throw error;
    }
  }

  /**
   * Updates the fuzzy search completed flag in the XML custom parts.
   * @param completed The value to set for the fuzzy search completed flag
   * @returns Promise that resolves when the flag is updated
   */
  public static async updateFuzzySearchCompleted(completed: boolean): Promise<void> {
    try {
      await Word.run(async (context) => {
        const customXmlParts = context.document.customXmlParts;
        customXmlParts.load("items");
        await context.sync();

        console.log(`Updating fuzzy search completed flag to: ${completed}`);

        // Find our custom XML part
        for (let i = 0; i < customXmlParts.items.length; i++) {
          const part = customXmlParts.items[i];
          try {
            part.load("namespaceUri");
            await context.sync();

            if (part.namespaceUri === this.NAMESPACE) {
              console.log("Found citation XML part, loading content for flag update");

              part.load("xml");
              await context.sync();

              if (part.xml && part.xml.trim() !== "") {
                const store = this.parseXmlToStore(part.xml);
                store.fuzzySearchCompleted = completed;
                store.lastUpdated = new Date().toISOString();

                console.log(`Setting fuzzySearchCompleted to ${completed}`);

                // Delete old part
                part.delete();
                await context.sync();

                // Add updated part
                const xml = this.storeToXml(store);
                customXmlParts.add(xml);
                await context.sync();

                console.log(`Successfully updated fuzzy search completed flag to: ${completed}`);
                return;
              }
            }
          } catch (partError) {
            console.error(`Error processing XML part ${i} during flag update:`, partError);
            continue;
          }
        }

        console.log("No citation XML part found to update flag");
      });
    } catch (error) {
      console.error("Error in updateFuzzySearchCompleted:", error);
      throw error;
    }
  }

  /**
   * OPTIMIZED: Clears all citation results from the document.
   * Reduced from N+2 syncs to 2 syncs + 1 conditional sync (only when deletions are queued)
   * by batching namespace loads.
   * @returns Promise that resolves when all results are cleared
   */
  public static async clearAllCitationResults(): Promise<void> {
    try {
      await Word.run(async (context) => {
        const customXmlParts = context.document.customXmlParts;
        customXmlParts.load("items");
        await context.sync(); // SYNC #1: Load all XML parts

        if (customXmlParts.items.length === 0) {
          console.log("No XML parts found in document");
          return;
        }

        console.log(
          `Found ${customXmlParts.items.length} XML parts, looking for citation data to clear`
        );

        // Batch load all namespace URIs at once (no sync in loop!)
        for (const part of customXmlParts.items) {
          part.load("namespaceUri");
        }
        await context.sync(); // SYNC #2: Load all namespaces at once

        // Find and delete our custom XML part (no additional sync needed for delete)
        let foundAndDeleted = false;
        for (const part of customXmlParts.items) {
          try {
            if (part.namespaceUri === this.NAMESPACE) {
              console.log("Found citation XML part, deleting it");
              part.delete();
              foundAndDeleted = true;
              // Don't break - there might be multiple parts with our namespace (cleanup)
            }
          } catch (partError) {
            console.error("Error checking/deleting XML part:", partError);
          }
        }

        if (foundAndDeleted) {
          await context.sync(); // SYNC #3: Only if we found something to delete
          console.log("Successfully cleared all citation results");
        } else {
          console.log("No citation XML part found to clear");
        }
      });
    } catch (error) {
      console.error("Error in clearAllCitationResults:", error);
      throw error;
    }
  }

  /**
   * Converts a CitationResultsStore to XML string
   * @param store The store to convert
   * @returns XML string representation
   */
  private static storeToXml(store: AnalyzeResultsStore): string {
    const escapeXml = (str: string | undefined | null): string => {
      if (str == null) {
        return "";
      }
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
    };

    console.log(
      "Converting store to XML, version:",
      store.version,
      "results:",
      store.results.length
    );

    let xml = `<?xml version="1.0" encoding="UTF-8"?>`;
    xml += `<${this.ROOT_ELEMENT} xmlns="${this.NAMESPACE}" version="${escapeXml(store.version)}" lastUpdated="${escapeXml(store.lastUpdated)}" fuzzySearchCompleted="${store.fuzzySearchCompleted ? "true" : "false"}">`;

    for (const result of store.results) {
      console.log(
        "Processing citation result:",
        result.documentReferenceId,
        "with",
        result.citations?.length || 0,
        "citations"
      );
      xml += `<CitationResult>`;
      xml += `<DocumentReferenceId>${escapeXml(result.documentReferenceId)}</DocumentReferenceId>`;
      const ct =
        result.citationType === CitationType.ParallelCites ? "ParallelCites" : "SingleCite";
      xml += `<CitationType>${escapeXml(ct)}</CitationType>`;
      xml += `<LastUpdated>${escapeXml(result.lastUpdated)}</LastUpdated>`;

      // Serialize citations array (LawCase[])
      xml += `<Citations>`;
      if (result.citations && result.citations.length > 0) {
        // Defensive: backend/merge may produce null entries
        const safeLawCases = result.citations.filter((c): c is LawCase => Boolean(c));

        for (const lawCase of safeLawCases) {
          xml += `<LawCase>`;
          xml += `<IdentifierOnDocument>${escapeXml(lawCase.identifierOnDocument)}</IdentifierOnDocument>`;
          xml += `<TitleOnDocument>${escapeXml(lawCase.titleOnDocument)}</TitleOnDocument>`;
          xml += `<Status>${escapeXml(String(lawCase.status))}</Status>`;

          xml += `<FoundDocs>`;
          if (lawCase.foundDocs && lawCase.foundDocs.length > 0) {
            const safeDocs = lawCase.foundDocs.filter((d): d is CaseReference => Boolean(d));
            for (const doc of safeDocs) {
              xml += `<Document>`;
              xml += `<DocId>${escapeXml(doc.docId)}</DocId>`;
              xml += `<Title>${escapeXml(doc.title)}</Title>`;
              xml += `<Identifier>${escapeXml(doc.identifier)}</Identifier>`;
              xml += `<Court>${escapeXml(doc.court)}</Court>`;
              xml += `<CourtAbbreviation>${escapeXml(doc.courtAbbreviation)}</CourtAbbreviation>`;
              xml += `<DocDate>${escapeXml(doc.docDate)}</DocDate>`;
              xml += `<Hyperlink>${escapeXml(doc.hyperlink)}</Hyperlink>`;
              xml += `</Document>`;
            }
          }
          xml += `</FoundDocs>`;
          xml += `</LawCase>`;
        }
      }
      xml += `</Citations>`;

      // Serialize resolvedLawCase (defensive: can be undefined, null, "none", or an object)
      const resolvedLawCase = result.resolvedLawCase;

      if (resolvedLawCase == null) {
        // null or undefined: skip
      } else if (resolvedLawCase === "none") {
        xml += `<ResolvedLawCase type="none" />`;
      } else {
        const resolved = resolvedLawCase;

        xml += `<ResolvedLawCase type="selected">`;
        xml += `<IdentifierOnDocument>${escapeXml(resolved.identifierOnDocument)}</IdentifierOnDocument>`;
        xml += `<TitleOnDocument>${escapeXml(resolved.titleOnDocument)}</TitleOnDocument>`;
        xml += `<Status>${escapeXml(String(resolved.status))}</Status>`;

        xml += `<FoundDocs>`;
        if (resolved.foundDocs && resolved.foundDocs.length > 0) {
          const safeDocs = resolved.foundDocs.filter((d): d is CaseReference => Boolean(d));
          for (const doc of safeDocs) {
            xml += `<Document>`;
            xml += `<DocId>${escapeXml(doc.docId)}</DocId>`;
            xml += `<Title>${escapeXml(doc.title)}</Title>`;
            xml += `<Identifier>${escapeXml(doc.identifier)}</Identifier>`;
            xml += `<Court>${escapeXml(doc.court)}</Court>`;
            xml += `<CourtAbbreviation>${escapeXml(doc.courtAbbreviation)}</CourtAbbreviation>`;
            xml += `<DocDate>${escapeXml(doc.docDate)}</DocDate>`;
            xml += `<Hyperlink>${escapeXml(doc.hyperlink)}</Hyperlink>`;
            xml += `</Document>`;
          }
        }
        xml += `</FoundDocs>`;

        xml += `</ResolvedLawCase>`;
      }

      xml += `</CitationResult>`;
    }

    xml += `</${this.ROOT_ELEMENT}>`;
    console.log("Generated XML length:", xml.length);
    return xml;
  }

  /**
   * Parses XML string to CitationResultsStore
   * @param xml The XML string to parse
   * @returns Parsed CitationResultsStore object
   */
  private static parseXmlToStore(xml: string): AnalyzeResultsStore {
    try {
      console.log("Parsing XML content, length:", xml.length);

      // Validate XML structure
      if (!xml || xml.trim() === "") {
        console.error("XML content is empty");
        return { version: this.VERSION, lastUpdated: new Date().toISOString(), results: [] };
      }

      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xml, "text/xml");

      // Check for parser errors
      const parseError = xmlDoc.querySelector("parsererror");
      if (parseError) {
        console.error("XML parsing error:", parseError.textContent);
        return { version: this.VERSION, lastUpdated: new Date().toISOString(), results: [] };
      }

      const rootElement = xmlDoc.documentElement;
      if (!rootElement) {
        console.error("No root element found in XML");
        return { version: this.VERSION, lastUpdated: new Date().toISOString(), results: [] };
      }

      const version = rootElement.getAttribute("version") || this.VERSION;
      const lastUpdated = rootElement.getAttribute("lastUpdated") || new Date().toISOString();
      const fuzzySearchCompleted = rootElement.getAttribute("fuzzySearchCompleted") === "true";

      console.log(
        `Parsed XML version: ${version}, lastUpdated: ${lastUpdated}, fuzzySearchCompleted: ${fuzzySearchCompleted}`
      );

      const results: AnalyzeResult[] = [];
      const citationResults = xmlDoc.getElementsByTagName("CitationResult");

      console.log(`Found ${citationResults.length} citation results in XML`);

      for (let i = 0; i < citationResults.length; i++) {
        try {
          const citationResult = citationResults[i];

          const documentReferenceId = this.getElementText(citationResult, "DocumentReferenceId");
          const citationTypeText = this.getElementText(citationResult, "CitationType").trim();
          const citationType: CitationType =
            citationTypeText === "ParallelCites" || citationTypeText === "1"
              ? CitationType.ParallelCites
              : CitationType.SingleCite;
          const lastUpdated = this.getElementText(citationResult, "LastUpdated");

          console.log(
            `Processing citation ${i + 1}: ${documentReferenceId}, type: ${citationType}`
          );

          // Parse citations array (LawCase[])
          const citations: LawCase[] = [];
          const citationsElement = citationResult.getElementsByTagName("Citations")[0];
          if (citationsElement) {
            const lawCases = citationsElement.getElementsByTagName("LawCase");

            for (let j = 0; j < lawCases.length; j++) {
              const lawCaseElement = lawCases[j];

              const identifierOnDocument = this.getElementText(
                lawCaseElement,
                "IdentifierOnDocument"
              );
              const titleOnDocument = this.getElementText(lawCaseElement, "TitleOnDocument");
              const statusText = this.getElementText(lawCaseElement, "Status");
              const status = parseInt(statusText, 10) as ResolutionStatus;

              const foundDocs: CaseReference[] = [];
              const foundDocsElement = lawCaseElement.getElementsByTagName("FoundDocs")[0];
              if (foundDocsElement) {
                const documents = foundDocsElement.getElementsByTagName("Document");

                for (let k = 0; k < documents.length; k++) {
                  const doc = documents[k];
                  foundDocs.push({
                    docId: this.getElementText(doc, "DocId"),
                    title: this.getElementText(doc, "Title"),
                    identifier: this.getElementText(doc, "Identifier"),
                    court: this.getElementText(doc, "Court"),
                    courtAbbreviation: this.getElementText(doc, "CourtAbbreviation"),
                    docDate: this.getElementText(doc, "DocDate"),
                    hyperlink: this.getElementText(doc, "Hyperlink"),
                  });
                }
              }

              citations.push({
                identifierOnDocument,
                titleOnDocument: titleOnDocument || undefined,
                status,
                foundDocs,
              });
            }
          }

          console.log(`Citation ${i + 1} has ${citations.length} law cases`);

          // Parse resolvedLawCase
          let resolvedLawCase: AnalyzeResult["resolvedLawCase"] = undefined;
          const resolvedElement = citationResult.getElementsByTagName("ResolvedLawCase")[0];
          if (resolvedElement) {
            const resolvedType = resolvedElement.getAttribute("type");
            if (resolvedType === "none") {
              resolvedLawCase = "none";
            } else if (resolvedType === "selected") {
              const identifierOnDocument = this.getElementText(
                resolvedElement,
                "IdentifierOnDocument"
              );
              const titleOnDocument = this.getElementText(resolvedElement, "TitleOnDocument");
              const statusText = this.getElementText(resolvedElement, "Status");
              const status = parseInt(statusText, 10) as ResolutionStatus;

              const foundDocs: CaseReference[] = [];
              const foundDocsElement = resolvedElement.getElementsByTagName("FoundDocs")[0];
              if (foundDocsElement) {
                const documents = foundDocsElement.getElementsByTagName("Document");

                for (let k = 0; k < documents.length; k++) {
                  const doc = documents[k];
                  foundDocs.push({
                    docId: this.getElementText(doc, "DocId"),
                    title: this.getElementText(doc, "Title"),
                    identifier: this.getElementText(doc, "Identifier"),
                    court: this.getElementText(doc, "Court"),
                    courtAbbreviation: this.getElementText(doc, "CourtAbbreviation"),
                    docDate: this.getElementText(doc, "DocDate"),
                    hyperlink: this.getElementText(doc, "Hyperlink"),
                  });
                }
              }

              resolvedLawCase = {
                identifierOnDocument,
                titleOnDocument: titleOnDocument || undefined,
                status,
                foundDocs,
              };
            }
          }

          results.push({
            documentReferenceId,
            citations,
            citationType,
            lastUpdated: lastUpdated || undefined,
            resolvedLawCase,
          });
        } catch (citationError) {
          console.error(`Error processing citation result ${i}:`, citationError);
          continue; // Skip this citation but continue with others
        }
      }

      console.log(`Successfully parsed ${results.length} citation results from XML`);
      return { version, lastUpdated, results, fuzzySearchCompleted };
    } catch (error) {
      console.error("Critical error parsing XML:", error);
      return { version: this.VERSION, lastUpdated: new Date().toISOString(), results: [] };
    }
  }

  /**
   * Helper method to get text content from an XML element
   * @param parent The parent element
   * @param tagName The tag name to find
   * @returns The text content or empty string
   */
  private static getElementText(parent: Element, tagName: string): string {
    const elements = parent.getElementsByTagName(tagName);
    if (elements.length > 0 && elements[0].textContent) {
      return elements[0].textContent;
    }
    return "";
  }
}
